import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

// Check if we're on autolisting.io domain (production) vs development
const isAutoListingDomain = typeof window !== 'undefined' && 
  window.location.hostname.endsWith('autolisting.io');

// Custom storage adapter that uses cookies with shared domain for cross-subdomain auth
// Only used in production on autolisting.io domain
const createCookieStorage = () => {
  const cookieDomain = '.autolisting.io';
  
  return {
    getItem: (key: string): string | null => {
      if (typeof document === 'undefined') return null;
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === key) {
          return decodeURIComponent(value);
        }
      }
      return null;
    },
    setItem: (key: string, value: string): void => {
      if (typeof document === 'undefined') return;
      const maxAge = 60 * 60 * 24 * 365; // 1 year
      const secure = window.location.protocol === 'https:' ? '; secure' : '';
      document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; domain=${cookieDomain}${secure}; samesite=lax`;
    },
    removeItem: (key: string): void => {
      if (typeof document === 'undefined') return;
      document.cookie = `${key}=; path=/; max-age=0; domain=${cookieDomain}`;
    },
  };
};

// Use localStorage in development (works in iframes), cookies in production (for cross-subdomain)
const getAuthStorage = () => {
  if (isAutoListingDomain) {
    console.log('[Auth] Using cookie storage for cross-subdomain auth');
    return createCookieStorage();
  }
  console.log('[Auth] Using localStorage for development');
  return typeof window !== 'undefined' ? window.localStorage : undefined;
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: getAuthStorage(),
    storageKey: 'sb-auth-token',
  }
});

// Schema-specific helpers for cleaner code
export const socialSchema = supabase.schema('social');
export const publicSchema = supabase.schema('public');
export const crmSchema = supabase.schema('crm');

// Convenience functions for common patterns
export const socialTable = (tableName: string) => socialSchema.from(tableName);
export const publicTable = (tableName: string) => publicSchema.from(tableName);
